/**
 * PATH: packages/tenant-shared/src/stores/uiStore.ts
 * FILE_ID: TENANTSHARED-UISTORE-001
 * PURPOSE: Tenant-agnostic UI store (sidebar collapsed flag, settings
 *          modal open flag) that every tenant's shell consumes.
 * WHY:     Byte-identical between newmark and sam pre-MR #7. Lifted
 *          so the next sidebar/settings UX change lands once.
 * RELATED:
 *   - apps/{newmark,sam}/src/shared/stores/uiStore.ts — thin
 *     re-export shims (post-MR #7).
 *
 * Sidebar preference model (2026-05-21):
 *   - `sidebarCollapsed` is the live state used by Sidebar.tsx.
 *   - `sidebarUserPreference` records EXPLICIT operator intent
 *     (clicked the collapse button or hit ⌘B). When set, it
 *     overrides any viewport-driven auto-collapse.
 *   - `setSidebarAuto()` is the viewport-driven setter — it
 *     no-ops the moment an explicit preference exists.
 *   - `clearSidebarPreference()` hands control back to the
 *     auto-tracker (shift-click on the collapse rail).
 *   - Both flags persist to localStorage so the rail survives a
 *     reload (Outlook-style: once you collapse it, it stays
 *     collapsed across sessions). See `useSidebarAutoCollapse`
 *     in the same package for the viewport hook that drives auto.
 */

import { create } from 'zustand'

const STORAGE_KEY = 'quotech.ui.sidebar.v1'
const NAV_STORAGE_KEY = 'quotech.ui.nav.v1'

export type SidebarPreference = 'expanded' | 'collapsed' | null

interface PersistedSidebarState {
  collapsed: boolean
  preference: SidebarPreference
}

function loadPersisted(): PersistedSidebarState {
  if (typeof window === 'undefined') return { collapsed: false, preference: null }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return { collapsed: false, preference: null }
    const parsed = JSON.parse(raw) as Partial<PersistedSidebarState>
    return {
      collapsed: Boolean(parsed.collapsed),
      preference:
        parsed.preference === 'expanded' || parsed.preference === 'collapsed'
          ? parsed.preference
          : null,
    }
  } catch {
    return { collapsed: false, preference: null }
  }
}

function persist(state: PersistedSidebarState): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    /* private-mode Safari, kiosk SPAs */
  }
}

/**
 * Sidebar nav-customization persisted shape (2026-05-21):
 *   - `favorites`  → ordered list of route paths the operator pinned
 *                    to the "Favorites" rail section (top of sidebar).
 *   - `hidden`     → list of route paths the operator hid from the rail
 *                    via right-click → "Hide from sidebar". The path is
 *                    NEVER removed from `MAIN_NAV`/`TOOLS_NAV` — that
 *                    would lose RBAC + dev flags. We just filter at
 *                    render time and surface a "Show hidden (N)" row
 *                    at the bottom of the rail so the operator can
 *                    recover anything they hide.
 *   - `sectionsCollapsed` → per-section open/closed map keyed by
 *                    section id ("favorites", "main", "tools"). Default
 *                    is "open" (Outlook-style collapsible groups, click
 *                    the header chevron to toggle).
 *
 * One JSON blob keeps the read/write atomic and avoids the multi-key
 * dance an operator on private-mode Safari would otherwise hit (each
 * localStorage write could throw independently). Same Outlook-style
 * "settings survive reload" expectation as `sidebarCollapsed`.
 */
interface PersistedNavState {
  favorites: string[]
  hidden: string[]
  sectionsCollapsed: Record<string, boolean>
}

function loadNavPersisted(): PersistedNavState {
  if (typeof window === 'undefined') {
    return { favorites: [], hidden: [], sectionsCollapsed: {} }
  }
  try {
    const raw = window.localStorage.getItem(NAV_STORAGE_KEY)
    if (!raw) return { favorites: [], hidden: [], sectionsCollapsed: {} }
    const parsed = JSON.parse(raw) as Partial<PersistedNavState>
    return {
      favorites: Array.isArray(parsed.favorites)
        ? parsed.favorites.filter((p): p is string => typeof p === 'string')
        : [],
      hidden: Array.isArray(parsed.hidden)
        ? parsed.hidden.filter((p): p is string => typeof p === 'string')
        : [],
      sectionsCollapsed:
        parsed.sectionsCollapsed && typeof parsed.sectionsCollapsed === 'object'
          ? Object.fromEntries(
              Object.entries(parsed.sectionsCollapsed).filter(
                ([k, v]) => typeof k === 'string' && typeof v === 'boolean',
              ),
            )
          : {},
    }
  } catch {
    return { favorites: [], hidden: [], sectionsCollapsed: {} }
  }
}

function persistNav(state: PersistedNavState): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(NAV_STORAGE_KEY, JSON.stringify(state))
  } catch {
    /* private-mode Safari, kiosk SPAs */
  }
}

interface UiState {
  sidebarCollapsed: boolean
  /** Explicit operator override — `null` means "follow viewport". */
  sidebarUserPreference: SidebarPreference
  toggleSidebar: () => void
  setSidebarCollapsed: (value: boolean) => void
  /** Viewport-driven setter. No-ops if the operator has set an
   *  explicit preference (see ``useSidebarAutoCollapse``). */
  setSidebarAuto: (value: boolean) => void
  /** Hand control back to the viewport tracker. */
  clearSidebarPreference: () => void
  /** Ordered list of route paths pinned to the "Favorites" rail
   *  section (top of sidebar). See `pinNavItem`/`unpinNavItem`. */
  navFavorites: string[]
  /** Route paths hidden from the rail via right-click. Recoverable
   *  via the "Show hidden" affordance at the bottom of the rail. */
  navHidden: string[]
  /** Per-section collapsed flag keyed by section id ("favorites",
   *  "main", "tools"). Default is open (false). */
  navSectionsCollapsed: Record<string, boolean>
  pinNavItem: (path: string) => void
  unpinNavItem: (path: string) => void
  hideNavItem: (path: string) => void
  unhideNavItem: (path: string) => void
  toggleNavSection: (sectionId: string) => void
  /** Wipe all customisations — restore the default rail. */
  resetNavCustomizations: () => void
  emailSidebarCollapsed: boolean
  toggleEmailSidebar: () => void
  settingsOpen: boolean
  settingsTab: string | null
  openSettings: (tab?: string) => void
  closeSettings: () => void
  /**
   * Quote Builder full-screen ("spreadsheet") working mode. When true,
   * the app shell hides its global chrome (sidebar, header, tab strip,
   * bottom dock) so the quote sheet uses nearly the whole viewport.
   *
   * Deliberately TRANSIENT (not persisted): a page reload must never
   * strand the shell with hidden chrome on a non-builder route. The
   * Quote Builder page owns the lifecycle — it resets this to false on
   * unmount so leaving the builder always restores normal chrome.
   */
  quoteFullscreen: boolean
  setQuoteFullscreen: (value: boolean) => void
  toggleQuoteFullscreen: () => void
}

const persisted = loadPersisted()
const navPersisted = loadNavPersisted()

export const useUiStore = create<UiState>((set, get) => ({
  sidebarCollapsed: persisted.collapsed,
  sidebarUserPreference: persisted.preference,
  navFavorites: navPersisted.favorites,
  navHidden: navPersisted.hidden,
  navSectionsCollapsed: navPersisted.sectionsCollapsed,
  toggleSidebar: () =>
    set((s) => {
      const next = !s.sidebarCollapsed
      persist({
        collapsed: next,
        preference: next ? 'collapsed' : 'expanded',
      })
      return {
        sidebarCollapsed: next,
        sidebarUserPreference: next ? 'collapsed' : 'expanded',
      }
    }),
  setSidebarCollapsed: (value) => {
    persist({
      collapsed: value,
      preference: value ? 'collapsed' : 'expanded',
    })
    set({
      sidebarCollapsed: value,
      sidebarUserPreference: value ? 'collapsed' : 'expanded',
    })
  },
  setSidebarAuto: (value) => {
    const state = get()
    if (state.sidebarUserPreference !== null) return
    if (state.sidebarCollapsed === value) return
    persist({ collapsed: value, preference: null })
    set({ sidebarCollapsed: value })
  },
  clearSidebarPreference: () => {
    persist({ collapsed: get().sidebarCollapsed, preference: null })
    set({ sidebarUserPreference: null })
  },
  // Pinning is idempotent + also unhides — operator's intent when they
  // pin an item from the rail is "I want this visible AND at the top",
  // not "pin it but keep it hidden". Same shape as Outlook's
  // Favorites contract.
  pinNavItem: (path) => {
    const state = get()
    const nextFavorites = state.navFavorites.includes(path)
      ? state.navFavorites
      : [...state.navFavorites, path]
    const nextHidden = state.navHidden.filter((p) => p !== path)
    if (
      nextFavorites === state.navFavorites &&
      nextHidden === state.navHidden
    ) {
      return
    }
    persistNav({
      favorites: nextFavorites,
      hidden: nextHidden,
      sectionsCollapsed: state.navSectionsCollapsed,
    })
    set({ navFavorites: nextFavorites, navHidden: nextHidden })
  },
  unpinNavItem: (path) => {
    const state = get()
    if (!state.navFavorites.includes(path)) return
    const nextFavorites = state.navFavorites.filter((p) => p !== path)
    persistNav({
      favorites: nextFavorites,
      hidden: state.navHidden,
      sectionsCollapsed: state.navSectionsCollapsed,
    })
    set({ navFavorites: nextFavorites })
  },
  // Hiding an item drops it from the main/tools sections; we keep
  // favorites authoritative for the Favorites section ("pinned items
  // are by definition visible"). If an operator hides an item that's
  // also pinned we silently unpin it too so the contract holds.
  hideNavItem: (path) => {
    const state = get()
    const nextHidden = state.navHidden.includes(path)
      ? state.navHidden
      : [...state.navHidden, path]
    const nextFavorites = state.navFavorites.filter((p) => p !== path)
    if (
      nextHidden === state.navHidden &&
      nextFavorites === state.navFavorites
    ) {
      return
    }
    persistNav({
      favorites: nextFavorites,
      hidden: nextHidden,
      sectionsCollapsed: state.navSectionsCollapsed,
    })
    set({ navHidden: nextHidden, navFavorites: nextFavorites })
  },
  unhideNavItem: (path) => {
    const state = get()
    if (!state.navHidden.includes(path)) return
    const nextHidden = state.navHidden.filter((p) => p !== path)
    persistNav({
      favorites: state.navFavorites,
      hidden: nextHidden,
      sectionsCollapsed: state.navSectionsCollapsed,
    })
    set({ navHidden: nextHidden })
  },
  toggleNavSection: (sectionId) => {
    const state = get()
    const current = state.navSectionsCollapsed[sectionId] ?? false
    const next = { ...state.navSectionsCollapsed, [sectionId]: !current }
    persistNav({
      favorites: state.navFavorites,
      hidden: state.navHidden,
      sectionsCollapsed: next,
    })
    set({ navSectionsCollapsed: next })
  },
  resetNavCustomizations: () => {
    persistNav({ favorites: [], hidden: [], sectionsCollapsed: {} })
    set({ navFavorites: [], navHidden: [], navSectionsCollapsed: {} })
  },
  emailSidebarCollapsed: false,
  toggleEmailSidebar: () => set((s) => ({ emailSidebarCollapsed: !s.emailSidebarCollapsed })),
  settingsOpen: false,
  settingsTab: null,
  openSettings: (tab) => set({ settingsOpen: true, settingsTab: tab ?? null }),
  closeSettings: () => set({ settingsOpen: false, settingsTab: null }),
  quoteFullscreen: false,
  setQuoteFullscreen: (value) =>
    set((s) => (s.quoteFullscreen === value ? s : { quoteFullscreen: value })),
  toggleQuoteFullscreen: () => set((s) => ({ quoteFullscreen: !s.quoteFullscreen })),
}))

if (typeof window !== 'undefined') {
  window.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === ',') {
      e.preventDefault()
      const { settingsOpen, openSettings, closeSettings } = useUiStore.getState()
      if (settingsOpen) closeSettings()
      else openSettings()
    }
  })
}
